import * as XLSX from 'xlsx';

/**
 * Generates a formatted filename with timestamp
 * @param {string} prefix - Filename prefix (e.g., "Alarm_Gecmisi", "Telemetri_Gecmisi")
 * @param {string} identifier - Optional identifier (e.g., nodeId, nodeName)
 * @returns {string} Formatted filename
 */
export const generateExcelFilename = (prefix, identifier = '') => {
  const now = new Date();
  const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const timeStr = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}`;

  const parts = [prefix];
  if (identifier) {
    parts.push(identifier);
  }
  parts.push(dateStr, timeStr);

  return `${parts.join('_')}.xlsx`;
};

/**
 * Converts DataGrid rows to Excel format
 * @param {Array} rows - DataGrid rows
 * @param {Array} columns - DataGrid columns
 * @param {Function} valueTransformer - Optional function to transform cell values
 * @returns {Array} Excel-formatted rows
 */
export const convertRowsToExcelFormat = (rows, columns, valueTransformer = null) => {
  return rows.map(row => {
    const excelRow = {};

    columns.forEach(column => {
      const columnName = column.headerName || column.field;
      let value = row[column.field];

      // Apply custom transformation if provided
      if (valueTransformer) {
        value = valueTransformer(value, column, row);
      }

      excelRow[columnName] = value !== undefined && value !== null ? value : '';
    });

    return excelRow;
  });
};

/**
 * Calculates optimal column widths based on content
 * @param {Array} excelRows - Excel-formatted rows
 * @param {Array} columns - DataGrid columns
 * @param {number} maxWidth - Maximum column width (default: 50)
 * @returns {Array} Column width configurations
 */
export const calculateColumnWidths = (excelRows, columns, maxWidth = 50) => {
  return columns.map(column => {
    const headerLength = (column.headerName || column.field).length;
    const maxContentLength = Math.max(
      ...excelRows.map(row => {
        const value = row[column.headerName || column.field];
        return value ? value.toString().length : 0;
      })
    );
    const width = Math.max(headerLength, maxContentLength, 10);
    return { wch: Math.min(width + 2, maxWidth) };
  });
};

/**
 * Creates and downloads an Excel file
 * @param {Array} rows - DataGrid rows
 * @param {Array} columns - DataGrid columns
 * @param {string} sheetName - Worksheet name
 * @param {string} fileName - Output filename
 * @param {Object} options - Additional options
 * @param {Function} options.valueTransformer - Custom value transformation function
 * @param {number} options.maxColumnWidth - Maximum column width
 * @param {Function} options.rowSorter - Custom row sorting function
 * @returns {Object} Result object with success status
 */
export const createAndDownloadExcel = (rows, columns, sheetName, fileName, options = {}) => {
  try {
    if (!rows || rows.length === 0) {
      return { success: false, error: 'No data to export' };
    }

    const {
      valueTransformer = null,
      maxColumnWidth = 50,
      rowSorter = null
    } = options;

    // Sort rows if sorter provided
    const processedRows = rowSorter ? [...rows].sort(rowSorter) : rows;

    // Convert rows to Excel format
    const excelRows = convertRowsToExcelFormat(processedRows, columns, valueTransformer);

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelRows);

    // Set column widths
    const columnWidths = calculateColumnWidths(excelRows, columns, maxColumnWidth);
    worksheet['!cols'] = columnWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Download file
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Excel export error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Creates an Excel file with multiple sheets
 * @param {Array} sheets - Array of sheet objects { name, rows, columns, options }
 * @param {string} fileName - Output filename
 * @returns {Object} Result object with success status
 */
export const createMultiSheetExcel = (sheets, fileName) => {
  try {
    if (!sheets || sheets.length === 0) {
      return { success: false, error: 'No sheets to export' };
    }

    const workbook = XLSX.utils.book_new();

    sheets.forEach(sheet => {
      const { name, rows, columns, options = {} } = sheet;

      if (!rows || rows.length === 0) {
        return; // Skip empty sheets
      }

      const {
        valueTransformer = null,
        maxColumnWidth = 50,
        rowSorter = null
      } = options;

      // Sort rows if sorter provided
      const processedRows = rowSorter ? [...rows].sort(rowSorter) : rows;

      // Convert rows to Excel format
      const excelRows = convertRowsToExcelFormat(processedRows, columns, valueTransformer);

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelRows);

      // Set column widths
      const columnWidths = calculateColumnWidths(excelRows, columns, maxColumnWidth);
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, name);
    });

    // Download file
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Multi-sheet Excel export error:', error);
    return { success: false, error: error.message };
  }
};
